import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Search, Upload, Pencil, Trash2, Loader2, Eye, Download, UserX } from 'lucide-react';
import { toast } from 'sonner';
import CustomerThreadPanel from '@/components/CustomerThreadPanel';

const SEGMENTS = { hot: 'Goracy', warm: 'Ciepli', cold: 'Zimni', dead: 'Martwi' };
const SEG_COLORS = {
  hot: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/20',
  warm: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20',
  cold: 'bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20',
  dead: 'bg-muted text-muted-foreground',
};

const emptyForm = { phone: '', email: '', first_name: '', last_name: '', city: '', region: '' };

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const perPage = 50;
  const [search, setSearch] = useState('');
  const [segFilter, setSegFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteImporting, setPasteImporting] = useState(false);
  const [pasteReport, setPasteReport] = useState(null);
  const [importGroup, setImportGroup] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [availableGroups, setAvailableGroups] = useState(['produkcyjna', 'testowa']);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [allCategories, setAllCategories] = useState([]);

  const fetchContacts = useCallback(async () => {
    try {
      const params = { search, skip: page * perPage, limit: perPage };
      if (segFilter !== 'all') params.segment = segFilter;
      if (groupFilter !== 'all') params.group = groupFilter;
      const [r, cats] = await Promise.all([api.get('/contacts', { params }), api.get('/interest-categories')]);
      setContacts(r.data.items);
      setTotal(r.data.total);
      setAllCategories(cats.data.items);
    } catch {} finally { setLoading(false); }
  }, [search, segFilter, groupFilter, page]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  useEffect(() => {
    api.get('/contacts/groups').then(r => setAvailableGroups(r.data)).catch(() => {});
  }, []);

  const getTargetGroup = () => {
    if (newGroupName.trim()) return newGroupName.trim();
    if (importGroup) return importGroup;
    return 'produkcyjna';
  };

  const openCreate = () => { setForm(emptyForm); setEditId(null); setDialogOpen(true); };
  const openEdit = (c) => { setForm({ phone: c.phone || '', email: c.email || '', first_name: c.first_name || '', last_name: c.last_name || '', city: c.city || '', region: c.region || '' }); setEditId(c.id); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) { await api.put(`/contacts/${editId}`, form); toast.success('Kontakt zaktualizowany'); }
      else { await api.post('/contacts', form); toast.success('Kontakt dodany'); }
      setDialogOpen(false); fetchContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac kontakt?')) return;
    try { await api.delete(`/contacts/${id}`); toast.success('Usunieto'); fetchContacts(); }
    catch { toast.error('Blad'); }
  };

  const openDetail = async (id) => {
    setDetailId(id);
    setDetailLoading(true);
    try { const r = await api.get(`/contacts/${id}/detail`); setDetail(r.data); }
    catch { toast.error('Blad'); }
    finally { setDetailLoading(false); }
  };

  const handlePasteImport = async () => {
    if (!pasteText.trim()) { toast.error('Wklej dane'); return; }
    const lines = pasteText.trim().split('\n').filter(l => l.trim());
    if (lines.length > 500) { toast.error(`Max 500 rekordow (masz ${lines.length}). Uzyj importu CSV.`); return; }
    setPasteImporting(true);
    setPasteReport(null);
    try {
      const targetGroup = getTargetGroup();
      const r = await api.post('/contacts/paste-import', { text: pasteText, group: targetGroup });
      setPasteReport(r.data);
      toast.success(`Zaimportowano ${r.data.imported} kontaktow do grupy "${targetGroup}"`);
      fetchContacts();
      // Refresh groups list
      api.get('/contacts/groups').then(r => setAvailableGroups(r.data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setPasteImporting(false); }
  };


  const handleGDPRExport = async (id) => {
    try { const r = await api.get(`/contacts/${id}/gdpr/export`); 
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `contact_${id}_export.json`; a.click();
      toast.success('Dane wyeksportowane');
    } catch { toast.error('Blad eksportu'); }
  };

  const handleGDPRDelete = async (id) => {
    try { await api.delete(`/contacts/${id}/gdpr/delete`); toast.success('Dane kontaktu usuniete (RODO)'); setDetailId(null); fetchContacts(); }
    catch { toast.error('Blad usuwania'); }
  };

  const toggleInterest = async (contactId, categoryId) => {
    const contact = detail || contacts.find(c => c.id === contactId);
    const hasInterest = (contact?.interests || []).some(i => i.category_id === categoryId);
    try {
      if (hasInterest) {
        await api.delete(`/contacts/${contactId}/interests/${categoryId}`);
      } else {
        await api.post(`/contacts/${contactId}/interests`, { category_ids: [categoryId] });
      }
      toast.success(hasInterest ? 'Zainteresowanie usuniete' : 'Zainteresowanie dodane');
      if (detailId) openDetail(detailId);
    } catch { toast.error('Blad'); }
  };

  const getCategoryName = (catId) => allCategories.find(c => c.id === catId)?.name || catId;
  const getCategoryColor = (catId) => allCategories.find(c => c.id === catId)?.color || '#6b7280';


  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('group', getTargetGroup());
      const r = await api.post('/contacts/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const targetGroup = getTargetGroup();
      toast.success(`Zaimportowano ${r.data.imported} kontaktow do grupy "${targetGroup}" (bledy: ${r.data.errors})`);
      setImportOpen(false);
      fetchContacts();
      api.get('/contacts/groups').then(r => setAvailableGroups(r.data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad importu'); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Kontakty</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} kontaktow w bazie</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setPasteOpen(true); setPasteText(''); setPasteReport(null); }} data-testid="contacts-paste-button">
            Wklej dane
          </Button>
          <Button variant="secondary" onClick={() => setImportOpen(true)} data-testid="contacts-import-button">
            <Upload className="h-4 w-4 mr-2" />Import CSV
          </Button>
          <Button onClick={openCreate} data-testid="contacts-add-button"><Plus className="h-4 w-4 mr-2" />Dodaj kontakt</Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Szukaj kontaktu..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} className="pl-9 bg-card/50" data-testid="contacts-search-input" />
        </div>
        <Select value={segFilter} onValueChange={v => { setSegFilter(v); setPage(0); }}>
          <SelectTrigger className="w-32" data-testid="contacts-segment-filter"><SelectValue placeholder="Segment" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie</SelectItem>
            <SelectItem value="hot">Goracy</SelectItem>
            <SelectItem value="warm">Ciepli</SelectItem>
            <SelectItem value="cold">Zimni</SelectItem>
            <SelectItem value="dead">Martwi</SelectItem>
          </SelectContent>
        </Select>
        <Select value={groupFilter || 'all'} onValueChange={v => setGroupFilter(v)}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Grupa" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie grupy</SelectItem>
            <SelectItem value="testowa">Testowa</SelectItem>
            <SelectItem value="produkcyjna">Produkcyjna</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border/50 bg-card/80">
        <CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div> : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Imie</TableHead>
                  <TableHead>Nazwisko</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Grupa</TableHead>
                  <TableHead>Segment</TableHead>
                  <TableHead className="w-24">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Brak kontaktow. Dodaj lub importuj!</TableCell></TableRow>
                ) : contacts.map(c => (
                  <TableRow key={c.id} className={`hover:bg-muted/30 transition-colors duration-150 ${c.is_blacklisted ? 'opacity-50' : ''}`}>
                    <TableCell className="font-medium">{c.first_name || '-'}</TableCell>
                    <TableCell className="text-sm">{c.last_name || '-'}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{c.phone || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">{c.email || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] cursor-pointer ${c.group === 'testowa' ? 'text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30' : 'text-[hsl(var(--success))] border-[hsl(var(--success))]/30'}`}
                        onClick={async () => { const newG = c.group === 'testowa' ? 'produkcyjna' : 'testowa'; await api.post('/contacts/bulk-group', {contact_ids: [c.id], group: newG}); fetchContacts(); }}>
                        {c.group || 'prod'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={SEG_COLORS[c.segment] || ''}>{SEGMENTS[c.segment] || c.segment}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDetail(c.id)}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > perPage && (
        <div className="flex items-center justify-between px-1">
          <p className="text-xs text-muted-foreground">
            Pokazuje {page * perPage + 1}-{Math.min((page + 1) * perPage, total)} z {total} kontaktow
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Poprzednia</Button>
            <span className="text-xs font-mono text-muted-foreground">Str. {page + 1} / {Math.ceil(total / perPage)}</span>
            <Button variant="outline" size="sm" disabled={(page + 1) * perPage >= total} onClick={() => setPage(p => p + 1)}>Nastepna</Button>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? 'Edytuj kontakt' : 'Nowy kontakt'}</DialogTitle><DialogDescription>Dane kontaktu</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Imie</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="contact-form-first-name" /></div>
              <div className="space-y-2"><Label>Nazwisko</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="contact-form-last-name" /></div>
            </div>
            <div className="space-y-2"><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+48..." data-testid="contact-form-phone" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="contact-form-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Miasto</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="contact-form-city" /></div>
              <div className="space-y-2"><Label>Wojewodztwo</Label><Input value={form.region} onChange={e => setForm({...form, region: e.target.value})} data-testid="contact-form-region" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="contact-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Import kontaktow</DialogTitle><DialogDescription>Wgraj plik CSV lub XLSX</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium">Grupa docelowa:</Label>
              <div className="flex gap-2">
                <Select value={importGroup} onValueChange={v => { setImportGroup(v); setNewGroupName(''); }}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Wybierz istniejaca grupe..." /></SelectTrigger>
                  <SelectContent>
                    {availableGroups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">lub nowa:</span>
                <Input placeholder="Nazwa nowej grupy..." value={newGroupName} onChange={e => { setNewGroupName(e.target.value); if (e.target.value) setImportGroup(''); }}
                  className="flex-1 h-8 text-sm" data-testid="import-new-group" />
              </div>
              <p className="text-[10px] text-muted-foreground">Grupa: <strong className="text-primary">{newGroupName.trim() || importGroup || 'produkcyjna'}</strong></p>
            </div>
            <p className="text-sm text-muted-foreground">Wgraj plik CSV lub XLSX. System automatycznie rozpozna kolumny: telefon, email, imie, nazwisko, miasto, wojewodztwo.</p>
            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center">
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-3">Przeciagnij plik lub kliknij</p>
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} className="max-w-xs mx-auto" data-testid="contacts-import-file-input" disabled={importing} />
              {importing && <div className="flex items-center justify-center gap-2 mt-3"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Importowanie...</span></div>}
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Paste Import Dialog */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Wklej dane kontaktow</DialogTitle>
            <DialogDescription>Szybki import przez wklejenie (max 500 rekordow / 50KB)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium">Grupa docelowa:</Label>
              <div className="flex gap-2">
                <Select value={importGroup} onValueChange={v => { setImportGroup(v); setNewGroupName(''); }}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Wybierz grupe..." /></SelectTrigger>
                  <SelectContent>
                    {availableGroups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">lub nowa:</span>
                <Input placeholder="Nazwa nowej grupy..." value={newGroupName} onChange={e => { setNewGroupName(e.target.value); if (e.target.value) setImportGroup(''); }}
                  className="flex-1 h-8 text-sm" data-testid="paste-new-group" />
              </div>
              <p className="text-[10px] text-muted-foreground">Importuj do: <strong className="text-primary">{newGroupName.trim() || importGroup || 'produkcyjna'}</strong></p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium">Wklej dane (jeden rekord na linie):</Label>
                <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} rows={12}
                  className="w-full rounded-lg border border-input bg-background/50 px-3 py-2 text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder={"jan@firma.pl;+48501234567;Jan;Kowalski;Warszawa\nanna@gmail.com\n+48601234567\ninfo@sklep.pl;+48701234567"}
                  data-testid="contacts-paste-textarea" />
                <p className="text-[10px] text-muted-foreground">{pasteText.split('\n').filter(l => l.trim()).length} rekordow | {pasteText.length} znakow</p>
              </div>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs font-medium mb-2">Akceptowane formaty:</p>
                  <div className="space-y-1 text-[11px] text-muted-foreground font-mono">
                    <p>jan@firma.pl</p>
                    <p>+48501234567</p>
                    <p>jan@firma.pl;+48501234567</p>
                    <p>+48501234567;jan@firma.pl;Jan;Kowalski;Warszawa</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs font-medium mb-2">Struktura pliku CSV:</p>
                  <div className="text-[11px] text-muted-foreground font-mono">
                    <p className="text-primary">telefon;email;imie;nazwisko;miasto</p>
                    <p>+48501234567;jan@firma.pl;Jan;Kowalski;Warszawa</p>
                    <p>+48601234567;anna@wp.pl;Anna;Nowak;Krakow</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-[hsl(var(--warning))]/5 border border-[hsl(var(--warning))]/20">
                  <p className="text-xs text-[hsl(var(--warning))]">Limity: max <strong>500</strong> rekordow / <strong>50KB</strong> przez wklejanie. Wieksze bazy &rarr; Import CSV (do 100K).</p>
                </div>
                {pasteReport && (
                  <div className="p-3 rounded-lg bg-[hsl(var(--success))]/5 border border-[hsl(var(--success))]/20 space-y-1">
                    <p className="text-xs font-medium">Raport:</p>
                    <p className="text-xs">Nowych: <strong className="text-[hsl(var(--success))]">{pasteReport.imported}</strong></p>
                    <p className="text-xs">Duplikatow: <strong>{pasteReport.duplicates}</strong></p>
                    <p className="text-xs">Czarna lista: <strong className="text-[hsl(var(--destructive))]">{pasteReport.blacklisted}</strong></p>
                    <p className="text-xs">Bledow: <strong>{pasteReport.errors}</strong></p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPasteOpen(false)}>Zamknij</Button>
            <Button onClick={handlePasteImport} disabled={pasteImporting || !pasteText.trim()} data-testid="contacts-paste-submit">
              {pasteImporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Importuj ({pasteText.split('\n').filter(l => l.trim()).length} rekordow)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Detail Sheet */}
      <Sheet open={!!detailId} onOpenChange={(open) => { if (!open) setDetailId(null); }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">Profil kontaktu</SheetTitle>
            <SheetDescription>Szczegoly i historia aktywnosci</SheetDescription>
          </SheetHeader>
          {detailLoading ? (
            <div className="space-y-4 mt-4"><Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-40" /></div>
          ) : detail ? (
            <div className="space-y-4 mt-4">
              <div>
                <h3 className="font-medium text-base">{detail.first_name} {detail.last_name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className={SEG_COLORS[detail.segment] || ''}>{SEGMENTS[detail.segment] || detail.segment}</Badge>
                  <span className="text-xs font-mono text-muted-foreground">{detail.activity_score} pkt</span>
                  {detail.is_blacklisted && <Badge variant="destructive" className="text-xs">Blacklisted</Badge>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground">Telefon</p>
                  <p className="text-sm font-mono">{detail.phone || '-'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm">{detail.email || '-'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground">Miasto</p>
                  <p className="text-sm">{detail.city || '-'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground">Zrodlo</p>
                  <p className="text-sm font-mono">{detail.source || '-'}</p>
                </div>
              </div>
              {detail.messages?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Historia SMS ({detail.messages.length})</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {detail.messages.map(m => (
                      <div key={m.id} className="flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-muted/30">
                        <Badge variant="outline" className={`text-[10px] w-14 justify-center ${m.status === 'sent' ? 'text-[hsl(var(--success))]' : m.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'}`}>{m.status}</Badge>
                        <span className="text-muted-foreground truncate">{m.message?.slice(0, 50)}...</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Interest Categories */}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Zainteresowania</p>
                <div className="flex flex-wrap gap-1.5">
                  {(detail.interests || []).map(i => (
                    <Badge key={i.category_id} variant="outline" className="text-xs cursor-pointer hover:bg-destructive/10" style={{ borderColor: getCategoryColor(i.category_id) + '40', color: getCategoryColor(i.category_id) }}
                      onClick={() => toggleInterest(detail.id, i.category_id)}>
                      {getCategoryName(i.category_id)} x
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {allCategories.filter(c => !(detail.interests || []).some(i => i.category_id === c.id)).map(c => (
                    <Button key={c.id} variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                      onClick={() => toggleInterest(detail.id, c.id)}>+ {c.name}</Button>
                  ))}
                </div>
              </div>
              {/* Wątek Customer Context Store */}
              <div className="border-t border-border/40 pt-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Wątek (Customer Context Store)</p>
                <CustomerThreadPanel contactId={detail.id} />
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => handleGDPRExport(detail.id)} data-testid="gdpr-export-button">
                  <Download className="h-3.5 w-3.5 mr-1.5" />Eksport RODO
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm" data-testid="gdpr-delete-button"><UserX className="h-3.5 w-3.5 mr-1.5" />Usun dane (RODO)</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Usunac dane kontaktu?</AlertDialogTitle>
                      <AlertDialogDescription>Ta operacja jest nieodwracalna. Wszystkie dane kontaktu, wiadomosci i zdarzenia zostana trwale usuniete zgodnie z art. 17 RODO.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Anuluj</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleGDPRDelete(detail.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Usun trwale</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
